/**
 * Design Tokens System
 * 
 * Comprehensive design tokens for the Al Maysarah IM Admin Dashboard.
 * These tokens provide a consistent foundation for theming and styling.
 */

export const tokens = {
  colors: {
    primary: {
      50: '#F5F5F7',
      100: '#E8E7EC',
      200: '#D1CFD9',
      300: '#A8A4B8',
      400: '#7F7997',
      500: '#3E3B5D', // Al Maysarah Navy - Main brand color
      600: '#32304A',
      700: '#262438',
      800: '#1A1825',
      900: '#0E0C13',
    },
    secondary: {
      50: '#E6F7F9',
      100: '#B3E9EE',
      200: '#80DBE3',
      300: '#4DCDD8',
      400: '#26B7C3', // Al Maysarah Teal - Main brand color
      500: '#1F929C',
      600: '#186E75',
      700: '#11494E',
      800: '#0A2527',
      900: '#030000',
    },
    success: {
      main: '#2E7D32', // Darker for better contrast (was #4CAF50)
      light: '#43A047', // Darker for better contrast (was #81C784)
      dark: '#1B5E20', // Darker for better contrast (was #388E3C)
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#E65100', // Darker for better contrast (was #FF9800)
      light: '#F57C00', // Darker for better contrast (was #FFB74D)
      dark: '#BF360C', // Darker for better contrast (was #F57C00)
      contrastText: '#FFFFFF', // Changed to white for better contrast (was #000000)
    },
    error: {
      main: '#C62828', // Darker for better contrast (was #F44336)
      light: '#D32F2F', // Darker for better contrast (was #E57373)
      dark: '#B71C1C', // Darker for better contrast (was #D32F2F)
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#1976D2', // Darker for better contrast (was #2196F3)
      light: '#1976D2', // Darker for better contrast (was #64B5F6)
      dark: '#0D47A1', // Darker for better contrast (was #1976D2)
      contrastText: '#FFFFFF',
    },
    neutral: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#616161', // Darker for better contrast (was #757575)
      700: '#424242',
      800: '#424242',
      900: '#212121',
    },
  },
  spacing: {
    unit: 4, // Base unit in pixels
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  typography: {
    fontFamily: {
      primary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      mono: '"Fira Code", "Consolas", monospace',
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      md: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },
  zIndex: {
    dropdown: 1000,
    sticky: 1100,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
    toast: 1600,
  },
  breakpoints: {
    mobile: 768,
    tablet: 1024,
    desktop: 1024,
  },
} as const;

// Type exports for TypeScript support
export type ColorScale = typeof tokens.colors.primary;
export type SemanticColor = typeof tokens.colors.success;
export type Spacing = keyof typeof tokens.spacing;
export type FontSize = keyof typeof tokens.typography.fontSize;
export type FontWeight = keyof typeof tokens.typography.fontWeight;
export type BorderRadius = keyof typeof tokens.borderRadius;
export type Shadow = keyof typeof tokens.shadows;
export type Transition = keyof typeof tokens.transitions;
export type ZIndex = keyof typeof tokens.zIndex;
export type Breakpoint = keyof typeof tokens.breakpoints;
